import { Link } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { useBookCall } from "../components/BookCallDialog";
import { useSeo, faqPageSchema, breadcrumbSchema } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ArrowUpRight } from "lucide-react";

const CONTENT = {
  en: {
    title: "FAQ — Furniture Assembly & Move-In Concierge Munich | MUConcierge",
    description:
      "Answers to frequently asked questions about IKEA furniture assembly, full apartment setup, and white-glove move-in concierge services in Munich for English-speaking expats.",
    h1: "Furniture assembly Munich — your questions, answered.",
    intro:
      "Everything English-speaking expats and professionals ask before booking an IKEA assembly service, a full apartment furniture setup, or a white-glove move-in concierge in Munich. Still unsure? Book a free 20-minute discovery call.",
    ctaBook: "Book a free discovery call",
    backHome: "← Back to MUConcierge home",
    sections: [
      {
        heading: "About MUConcierge",
        faqs: [
          {
            q: "What is MUConcierge?",
            a: "MUConcierge is an end-to-end furniture assembly and move-in concierge service in Munich, built specifically for English-speaking expats and international professionals. We plan, coordinate deliveries, assemble flat-pack furniture (IKEA, Home24, XXXLutz, BoConcept and more), and set up your apartment so you can move in stress-free.",
          },
          {
            q: "Who do you work with?",
            a: "Expats moving to Munich, professionals starting a new role, families arriving from abroad, corporate HR teams, and furnished-apartment landlords. We also help residents who are moving within Munich or upgrading their home after settling in.",
          },
          {
            q: "Which neighborhoods in Munich do you serve?",
            a: "We operate across all of Munich — Schwabing, Maxvorstadt, Au-Haidhausen, Glockenbach, Bogenhausen, Sendling, Neuhausen, Pasing — and the wider metropolitan area including Garching, Unterföhring, and Grünwald.",
          },
        ],
      },
      {
        heading: "IKEA assembly service",
        faqs: [
          {
            q: "Do you assemble all IKEA furniture?",
            a: "Yes. Our IKEA assembly team handles PAX, PLATSA and BRIMNES wardrobes, MALM, HEMNES and SONGESAND beds, BEKANT, MICKE and ALEX desks, BILLY bookcases, BESTÅ systems, METOD kitchen units and most other IKEA ranges. We are professional IKEA furniture assembly experts in Munich.",
          },
          {
            q: "How much does IKEA assembly in Munich cost?",
            a: "Single-room IKEA assembly starts from €450 with fixed transparent pricing — no hourly billing surprises. Full IKEA apartment assembly starts from €1,290. We quote each flat-pack furniture assembly job upfront so you know the total before we begin.",
          },
          {
            q: "Can you pick up my IKEA order or receive a delivery?",
            a: "Yes. We coordinate pickups from IKEA München-Brunnthal or IKEA Eching and receive IKEA home deliveries on your behalf — useful when you are still abroad or finishing the lease handover.",
          },
          {
            q: "What happens if a part is missing or damaged?",
            a: "Missing or damaged parts are common with flat-pack furniture. We handle return runs and IKEA exchange logistics. The first replacement trip within 5 km is included in our IKEA assembly service.",
          },
          {
            q: "Do you assemble non-IKEA flat-pack furniture too?",
            a: "Yes — we are full furniture assembly handymen in Munich. Home24, XXXLutz, BoConcept, Wayfair, Made.com and other brands are covered under our M and L packages.",
          },
        ],
      },
      {
        heading: "Full apartment setup & move-in",
        faqs: [
          {
            q: "What is included in a full apartment setup?",
            a: "Multi-room planning, delivery management, full furniture assembly and installation, room arrangement (bedroom, living room, workspace), packaging removal and recycling, and a final walkthrough. Our Munich apartment setup service is designed to feel like white-glove move-in concierge.",
          },
          {
            q: "Can you set everything up before I arrive in Munich?",
            a: "Yes. Pre-arrival planning is a core part of our expat relocation support. With apartment access we coordinate deliveries, assemble furniture and stage rooms so you walk into a move-in ready home on day one.",
          },
          {
            q: "Do you support corporate relocations and HR teams?",
            a: "Yes. We standardize apartment setups for corporate relocation packages — repeatable scopes, single point of contact, English-first communication, and consolidated invoicing.",
          },
          {
            q: "How fast can you get my apartment ready?",
            a: "When inventory is available, we can deliver a 48-hour rush apartment setup in Munich. Standard turnarounds are 5–10 days depending on scope.",
          },
        ],
      },
      {
        heading: "Pricing, booking & policies",
        faqs: [
          {
            q: "How do I book a furniture assembly service in Munich?",
            a: "Use the “Book consultation” button on this site to schedule a free 20-minute discovery call. You can share an IKEA order list, photos of the apartment, or simply describe your situation. We respond within 4 business hours.",
          },
          {
            q: "Do you offer weekend appointments?",
            a: "Yes. Saturday slots are bookable as standard, and Sunday assembly is available on request for time-critical move-ins.",
          },
          {
            q: "Are you insured?",
            a: "Yes. We carry full property and worker-liability insurance for every IKEA assembly, furniture installation and apartment setup job in Munich.",
          },
          {
            q: "Do you install kitchens, electrical, or plumbing?",
            a: "No. Regulated trades (Elektriker, Klempner, gas, kitchen connection) are excluded — we focus on furniture assembly and move-in setup.",
          },
          {
            q: "Can you assist after I've already moved in?",
            a: "Yes. Many clients book us after the move to add a wardrobe, swap a desk, dismantle old furniture, or reconfigure rooms when family or work-from-home needs change.",
          },
        ],
      },
    ],
  },
  de: {
    title: "FAQ — Möbelmontage & Move-In Concierge München | MUConcierge",
    description:
      "Antworten auf häufige Fragen zur IKEA Möbelmontage, kompletten Wohnungseinrichtung und zum Münchner Move-In Concierge-Service für englischsprachige Expats und internationale Fachkräfte.",
    h1: "Möbelmontage München — Ihre Fragen, beantwortet.",
    intro:
      "Alles, was englischsprachige Expats und Fachkräfte vor der Buchung einer IKEA Möbelmontage, einer kompletten Wohnungseinrichtung oder eines White-Glove Move-In-Concierge in München wissen wollen. Noch unsicher? Buchen Sie ein kostenloses 20-Minuten-Beratungsgespräch.",
    ctaBook: "Kostenloses Beratungsgespräch buchen",
    backHome: "← Zurück zur MUConcierge-Startseite",
    sections: [
      {
        heading: "Über MUConcierge",
        faqs: [
          {
            q: "Was ist MUConcierge?",
            a: "MUConcierge ist ein End-to-End Möbelmontage- und Move-In Concierge-Service in München, speziell für englischsprachige Expats und internationale Fachkräfte. Wir planen, koordinieren Lieferungen, montieren Flat-Pack-Möbel (IKEA, Home24, XXXLutz, BoConcept u. a.) und richten Ihre Wohnung ein, damit Sie stressfrei einziehen können.",
          },
          {
            q: "Mit wem arbeiten Sie?",
            a: "Mit Expats, die nach München ziehen, Fachkräften mit neuer Stelle, internationalen Familien, Corporate-HR-Teams und Vermietern möblierter Wohnungen. Auch Münchner, die innerhalb der Stadt umziehen oder ihre Wohnung nach dem Einzug optimieren möchten.",
          },
          {
            q: "Welche Stadtteile Münchens decken Sie ab?",
            a: "Wir arbeiten in ganz München — Schwabing, Maxvorstadt, Au-Haidhausen, Glockenbach, Bogenhausen, Sendling, Neuhausen, Pasing — und im Großraum inkl. Garching, Unterföhring und Grünwald.",
          },
        ],
      },
      {
        heading: "IKEA Möbelmontage",
        faqs: [
          {
            q: "Montieren Sie alle IKEA-Möbel?",
            a: "Ja. Unser IKEA Aufbauservice in München übernimmt PAX-, PLATSA- und BRIMNES-Kleiderschränke, MALM-, HEMNES- und SONGESAND-Betten, BEKANT-, MICKE- und ALEX-Schreibtische, BILLY-Regale, BESTÅ-Systeme, METOD-Küchen und die meisten anderen IKEA-Serien.",
          },
          {
            q: "Was kostet die IKEA Möbelmontage in München?",
            a: "Eine IKEA Möbelmontage für ein Zimmer beginnt ab €450 — Festpreis, transparent, keine Stundenüberraschungen. Komplette IKEA Wohnungseinrichtung ab €1.290. Jeder Auftrag wird im Voraus kalkuliert.",
          },
          {
            q: "Können Sie meine IKEA-Bestellung abholen oder annehmen?",
            a: "Ja. Wir koordinieren Abholungen bei IKEA München-Brunnthal oder IKEA Eching und nehmen IKEA-Hauslieferungen für Sie an — ideal, wenn Sie noch im Ausland sind oder die Wohnungsübergabe läuft.",
          },
          {
            q: "Was, wenn ein Teil fehlt oder beschädigt ist?",
            a: "Fehlende oder beschädigte Teile sind bei Flat-Pack-Möbeln üblich. Wir übernehmen Retouren und IKEA-Umtauschlogistik. Die erste Ersatzfahrt innerhalb von 5 km ist im IKEA Aufbauservice inklusive.",
          },
          {
            q: "Montieren Sie auch Möbel anderer Marken?",
            a: "Ja — wir sind ein vollständiger Möbelmontageservice in München. Home24, XXXLutz, BoConcept, Wayfair, Made.com und andere Marken sind in unseren M- und L-Paketen enthalten.",
          },
        ],
      },
      {
        heading: "Komplette Wohnungseinrichtung & Einzug",
        faqs: [
          {
            q: "Was ist in der kompletten Wohnungseinrichtung enthalten?",
            a: "Mehrzimmer-Planung, Liefermanagement, komplette Möbelmontage und -installation, Raumeinrichtung (Schlafzimmer, Wohnzimmer, Homeoffice), Verpackungsentsorgung und Recycling sowie Abschluss-Begehung. Unser Wohnungseinrichtungsservice in München fühlt sich wie White-Glove Move-In Concierge an.",
          },
          {
            q: "Können Sie alles vor meiner Ankunft in München einrichten?",
            a: "Ja. Pre-Arrival-Planung ist Kernbestandteil unseres Expat-Relocation-Supports. Mit Wohnungszugang koordinieren wir Lieferungen, montieren Möbel und richten Räume ein, sodass Sie in ein einzugsfertiges Zuhause kommen.",
          },
          {
            q: "Unterstützen Sie Corporate-Relocations und HR-Teams?",
            a: "Ja. Wir standardisieren Wohnungseinrichtungen für Corporate-Relocation-Pakete — wiederholbare Leistungsumfänge, ein Ansprechpartner, englischsprachige Kommunikation und konsolidierte Rechnung.",
          },
          {
            q: "Wie schnell kann meine Wohnung fertig sein?",
            a: "Bei verfügbarer Ware liefern wir einen 48-Stunden-Express-Wohnungseinrichtungsservice in München. Reguläre Vorlaufzeiten liegen bei 5–10 Tagen je nach Umfang.",
          },
        ],
      },
      {
        heading: "Preise, Buchung & Bedingungen",
        faqs: [
          {
            q: "Wie buche ich einen Möbelmontageservice in München?",
            a: "Klicken Sie auf „Beratung buchen“ und vereinbaren Sie ein kostenloses 20-Minuten-Beratungsgespräch. Sie können eine IKEA-Bestellliste, Fotos der Wohnung oder einfach Ihre Situation schildern. Antwort innerhalb von 4 Werktagsstunden.",
          },
          {
            q: "Bieten Sie Wochenend-Termine an?",
            a: "Ja. Samstagstermine sind regulär buchbar, Sonntagsmontage auf Anfrage für zeitkritische Einzüge.",
          },
          {
            q: "Sind Sie versichert?",
            a: "Ja. Wir sind voll sach- und personenhaftpflichtversichert für jede IKEA-Montage, Möbelinstallation und Wohnungseinrichtung in München.",
          },
          {
            q: "Installieren Sie Küchen, Elektrik oder Sanitäranlagen?",
            a: "Nein. Zulassungspflichtige Gewerke (Elektriker, Klempner, Gas, Kücheninstallation) sind ausgeschlossen — wir fokussieren auf Möbelmontage und Move-In-Einrichtung.",
          },
          {
            q: "Helfen Sie auch nach dem Einzug?",
            a: "Ja. Viele Kunden buchen uns nach dem Einzug, um einen Schrank zu ergänzen, einen Schreibtisch zu tauschen, alte Möbel zu demontieren oder Räume umzukonfigurieren.",
          },
        ],
      },
    ],
  },
};

export default function FaqPage({ lang = "en" }) {
  const t = CONTENT[lang];
  const tag = lang === "de" ? "Häufige Fragen" : "Frequently Asked Questions";
  const { openDialog } = useBookCall();
  const homePath = lang === "de" ? "/de" : "/";
  const origin = typeof window !== "undefined" ? window.location.origin : "https://muconcierge.com";
  const path = lang === "de" ? "/de/faq" : "/faq";
  const allFaqs = t.sections.flatMap((s) => s.faqs);

  useSeo({
    title: t.title,
    description: t.description,
    path,
    lang,
    alternates: { en: "/faq", de: "/de/faq" },
    jsonLd: [
      faqPageSchema(allFaqs),
      breadcrumbSchema([
        { name: lang === "de" ? "Startseite" : "Home", url: origin + homePath },
        { name: "FAQ", url: origin + path },
      ]),
    ],
  });

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SiteHeader />

      <section className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 pt-20 md:pt-24 pb-10" data-testid="faq-hero">
        <p className="tag-line text-[#C26D5C] mb-4">{tag}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A201C] tracking-tight leading-[1.05]">
          {t.h1}
        </h1>
        <p className="mt-6 text-[#5C635A] leading-relaxed font-light text-lg max-w-3xl">{t.intro}</p>
        <div className="mt-8">
          <Button
            size="lg"
            onClick={() => openDialog({ source: `faq-${lang}-top`, service: lang === "de" ? "Beratungsgespräch" : "Discovery call" })}
            className="rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6] px-7 py-5"
            data-testid="faq-cta-top"
          >
            {t.ctaBook}
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 pb-20" data-testid="faq-sections">
        {t.sections.map((sec, si) => (
          <div key={sec.heading} className="mt-12" data-testid={`faq-section-${si}`}>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1A201C] mb-5 border-b border-[#E5E2DC] pb-3">{sec.heading}</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {sec.faqs.map((f, i) => (
                <AccordionItem key={i} value={`s${si}-i${i}`} className="border border-[#E5E2DC] bg-white px-6">
                  <AccordionTrigger className="font-serif text-lg text-[#1A201C] hover:no-underline text-left" data-testid={`faq-q-${si}-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#5C635A] leading-relaxed font-light">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </section>

      <section className="bg-[#1C3326]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 py-16 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FAF9F6] tracking-tight leading-[1.1]">
            {lang === "de" ? "Noch Fragen?" : "Still have questions?"}
          </h2>
          <p className="mt-4 text-[#FAF9F6]/80 max-w-xl mx-auto font-light">
            {lang === "de"
              ? "Buchen Sie ein kostenloses 20-Minuten-Beratungsgespräch — Antwort innerhalb von 4 Werktagsstunden."
              : "Book a free 20-minute discovery call — we reply within 4 business hours."}
          </p>
          <Button
            size="lg"
            onClick={() => openDialog({ source: `faq-${lang}-final`, service: lang === "de" ? "Beratungsgespräch" : "Discovery call" })}
            className="mt-8 rounded-sm bg-[#FAF9F6] text-[#1C3326] hover:bg-[#E8C9B6] px-10 py-6 text-base"
            data-testid="faq-cta-final"
          >
            {t.ctaBook}
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
          <div className="mt-6">
            <Link to={homePath} className="text-xs text-[#FAF9F6]/60 underline underline-offset-4 hover:text-[#E8C9B6]" data-testid="faq-back-home">
              {t.backHome}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
